import { useEffect } from 'react';

const EnhancedAnimations = () => {
  useEffect(() => {
    // ========== 1. SCROLL-TRIGGERED ANIMATIONS ==========
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const animateOnScroll = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    // Observe all elements with data-animate attribute
    document.querySelectorAll('[data-animate]').forEach(el => {
      animateOnScroll.observe(el);
    });

    // ========== 2. SMOOTH PARALLAX MOUSE MOVEMENT ==========
    let mouseX = 0;
    let mouseY = 0;
    let currentX = 0;
    let currentY = 0;

    const handleMouseMove = (e) => {
      mouseX = (e.clientX / window.innerWidth - 0.5) * 2;
      mouseY = (e.clientY / window.innerHeight - 0.5) * 2;
    };

    const animateParallax = () => {
      currentX += (mouseX - currentX) * 0.1;
      currentY += (mouseY - currentY) * 0.1;

      document.querySelectorAll('[data-parallax]').forEach(el => {
        const speed = el.dataset.parallax || 20;
        const x = currentX * speed;
        const y = currentY * speed;
        el.style.transform = `translate(${x}px, ${y}px)`;
      });

      requestAnimationFrame(animateParallax);
    };

    window.addEventListener('mousemove', handleMouseMove);
    animateParallax();

    // ========== 3. NUMBER COUNTER ANIMATION ==========
    const animateCounter = (element, target, duration = 2000) => {
      const start = 0;
      const increment = target / (duration / 16);
      let current = start;

      const updateCounter = () => {
        current += increment;
        if (current < target) {
          element.textContent = Math.floor(current).toLocaleString();
          requestAnimationFrame(updateCounter);
        } else {
          element.textContent = target.toLocaleString();
        }
      };

      updateCounter();
    };

    const counterObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !entry.target.classList.contains('counted')) {
          entry.target.classList.add('counted');
          const target = parseInt(entry.target.dataset.count);
          animateCounter(entry.target, target);
        }
      });
    }, { threshold: 0.5 });

    document.querySelectorAll('[data-count]').forEach(el => {
      counterObserver.observe(el);
    });

    // ========== 4. MAGNETIC BUTTONS ==========
    const magneticButtons = document.querySelectorAll('[data-magnetic]');
    
    magneticButtons.forEach(button => {
      button.addEventListener('mousemove', (e) => {
        const rect = button.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        
        button.style.transform = `translate(${x * 0.3}px, ${y * 0.3}px) scale(1.05)`;
      });

      button.addEventListener('mouseleave', () => {
        button.style.transform = 'translate(0, 0) scale(1)';
      });
    });

    // ========== 5. FLOATING PARTICLES SYSTEM ==========
    const createParticles = () => {
      const particleContainer = document.createElement('div');
      particleContainer.className = 'particle-container';
      particleContainer.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 1;
        overflow: hidden;
      `;

      for (let i = 0; i < 30; i++) {
        const particle = document.createElement('div');
        particle.className = 'floating-particle';
        particle.style.cssText = `
          position: absolute;
          width: ${Math.random() * 4 + 2}px;
          height: ${Math.random() * 4 + 2}px;
          background: radial-gradient(circle, rgba(239, 191, 4, 0.8) 0%, rgba(239, 191, 4, 0.2) 70%, transparent 100%);
          border-radius: 50%;
          left: ${Math.random() * 100}%;
          top: ${Math.random() * 100}%;
          animation: float-particle ${Math.random() * 10 + 15}s linear infinite;
          animation-delay: ${Math.random() * 5}s;
          opacity: ${Math.random() * 0.5 + 0.3};
          box-shadow: 0 0 ${Math.random() * 10 + 5}px rgba(239, 191, 4, 0.6);
        `;
        particleContainer.appendChild(particle);
      }

      document.body.appendChild(particleContainer);
    };

    // Add particle animation keyframes
    const style = document.createElement('style');
    style.textContent = `
      @keyframes float-particle {
        0% {
          transform: translate(0, 0);
        }
        25% {
          transform: translate(60px, -60px);
        }
        50% {
          transform: translate(120px, 0);
        }
        75% {
          transform: translate(60px, 60px);
        }
        100% {
          transform: translate(0, 0);
        }
      }

      @keyframes slide-in-left {
        from {
          opacity: 0;
          transform: translateX(-100px);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }

      @keyframes slide-in-right {
        from {
          opacity: 0;
          transform: translateX(100px);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }

      @keyframes slide-in-up {
        from {
          opacity: 0;
          transform: translateY(50px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @keyframes fade-in-scale {
        from {
          opacity: 0;
          transform: scale(0.8);
        }
        to {
          opacity: 1;
          transform: scale(1);
        }
      }

      [data-animate] {
        opacity: 0;
      }

      [data-animate].animate-in {
        animation-duration: 0.8s;
        animation-fill-mode: both;
        animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
      }

      [data-animate="slide-left"].animate-in {
        animation-name: slide-in-left;
      }

      [data-animate="slide-right"].animate-in {
        animation-name: slide-in-right;
      }

      [data-animate="slide-up"].animate-in {
        animation-name: slide-in-up;
      }

      [data-animate="fade-scale"].animate-in {
        animation-name: fade-in-scale;
      }

      [data-magnetic] {
        transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      }
    `;
    document.head.appendChild(style);

    createParticles();

    // ========== 6. SUBTLE LIFT EFFECT ON CARDS (no rotation) ==========
    const tiltCards = document.querySelectorAll('[data-tilt]');

    tiltCards.forEach(card => {
      card.addEventListener('mouseenter', () => {
        card.style.transform = 'translateY(-4px) scale(1.01)';
      });
      card.addEventListener('mouseleave', () => {
        card.style.transform = 'translateY(0) scale(1)';
      });
    });

    // ========== 7. SMOOTH SCROLL ==========
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function (e) {
        const href = this.getAttribute('href');
        if (href !== '#') {
          e.preventDefault();
          const target = document.querySelector(href);
          if (target) {
            target.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      });
    });

    // Cleanup
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.querySelector('.particle-container')?.remove();
    };
  }, []);

  return null;
};

export default EnhancedAnimations;
